import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { alpha } from '@mui/material/styles';
import { apiService, extractApiErrorMessage } from '../services/apiService';
import { MAX_CUSTOM_FUNCTIONS_PER_USER, invalidateCustomFunctionsCache } from '../services/functionService';
import type { AgentMessageResponse } from '../types/agent';
import { isAgentFailureMessage } from '../utils/agentChatMessageStyle';
import {
  buildStaticFunctionValuePayload,
  normalizeAgentFunctionName,
  stripAgentFunctionDollar,
} from '../utils/agentFunctionUtils';
import { AgentInlineSelect, AgentTextField } from './ai-agent-ui';

export type AgentWidgetCreateFunctionFormProps = {
  chatId?: number | null;
  defaultName?: string;
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetCreateFunctionForm: React.FC<AgentWidgetCreateFunctionFormProps> = ({
  chatId,
  defaultName = '',
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const [name, setName] = useState(stripAgentFunctionDollar(defaultName));
  const [fnType, setFnType] = useState<'JS' | 'STATIC'>('JS');
  const [valueType, setValueType] = useState<'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON'>('STRING');
  const [staticValue, setStaticValue] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [code, setCode] = useState('return args[0];');
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(stripAgentFunctionDollar(defaultName));
  }, [defaultName]);

  useEffect(() => {
    void (async () => {
      try {
        const listRes = await apiService.get('/functions');
        const activeCount =
          typeof listRes?.data?.activeCount === 'number'
            ? listRes.data.activeCount
            : Array.isArray(listRes?.data?.functions)
              ? listRes.data.functions.length
              : 0;
        const maxAllowed =
          typeof listRes?.data?.maxAllowed === 'number'
            ? listRes.data.maxAllowed
            : MAX_CUSTOM_FUNCTIONS_PER_USER;
        if (activeCount >= maxAllowed) {
          setError(
            `Custom function limit exceeded (${maxAllowed} per user). Delete an existing function to create a new one.`,
          );
        }
      } catch {
        /* save will surface server errors */
      }
    })();
  }, []);

  const handleGenerate = async () => {
    const trimmedPrompt = aiPrompt.trim();
    if (!trimmedPrompt) {
      setError('Describe what the function should do, then generate.');
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const res = await apiService.post(
        '/functions/generate-code',
        {
          prompt: trimmedPrompt,
          functionName: stripAgentFunctionDollar(name) || undefined,
        },
        { timeout: 300000 },
      );
      const generated = res?.data?.code;
      if (!generated || typeof generated !== 'string') {
        throw new Error('AI did not return function code');
      }
      setFnType('JS');
      setCode(generated);
    } catch (e: unknown) {
      setError(extractApiErrorMessage(e) || (e instanceof Error ? e.message : 'Failed to generate function code.'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    const bareName = stripAgentFunctionDollar(name);
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(bareName)) {
      setError('Name must start with a letter and use only letters, numbers, or underscores.');
      return;
    }

    let value: string | undefined;
    let fnCode: string | undefined;
    try {
      if (fnType === 'STATIC') {
        value = buildStaticFunctionValuePayload(valueType, staticValue);
        if (!value.trim()) {
          setError('Value is required for a static function.');
          return;
        }
      } else {
        fnCode = code.trim();
        if (!fnCode || !fnCode.includes('return')) {
          setError('JavaScript function must include a return statement.');
          return;
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Invalid static value.');
      return;
    }

    const display = normalizeAgentFunctionName(bareName);
    const userText = `Create custom function ${display} (${fnType}).`;
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await apiService.agentMessage({
        action: 'create_custom_function',
        chatId: chatId ?? undefined,
        payload: {
          name: bareName,
          type: fnType,
          ...(fnType === 'STATIC' ? { value } : { code: fnCode }),
        },
      });
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      const failed = Boolean(res.error) || isAgentFailureMessage(res.reply || '', res.actions);
      if (failed) {
        const reply = (res.reply || res.error || '').trim();
        setError(reply || 'Could not create custom function.');
      } else {
        invalidateCustomFunctionsCache();
        onClose();
      }
      await onAgentReply(res);
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || generating;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Create custom function
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
        <AgentTextField
          id="widget-create-fn-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="myHelper → $myHelper"
          fullWidth
          disabled={busy}
        />
        <AgentInlineSelect
          id="widget-create-fn-type"
          label="Type"
          fullWidth
          value={fnType}
          disabled={busy}
          options={[
            { value: 'JS', label: 'JavaScript' },
            { value: 'STATIC', label: 'Static value' },
          ]}
          onChange={(raw) => setFnType(raw === 'STATIC' ? 'STATIC' : 'JS')}
        />

        {fnType === 'STATIC' ? (
          <>
            <AgentInlineSelect
              id="widget-create-fn-value-type"
              label="Value type"
              fullWidth
              value={valueType}
              disabled={busy}
              options={[
                { value: 'STRING', label: 'String' },
                { value: 'NUMBER', label: 'Number' },
                { value: 'BOOLEAN', label: 'Boolean' },
                { value: 'JSON', label: 'JSON' },
              ]}
              onChange={(raw) =>
                setValueType(
                  raw === 'NUMBER' || raw === 'BOOLEAN' || raw === 'JSON' ? raw : 'STRING',
                )
              }
            />
            {valueType === 'BOOLEAN' ? (
              <AgentInlineSelect
                id="widget-create-fn-bool-value"
                label="Value"
                fullWidth
                value={staticValue === 'true' ? 'true' : 'false'}
                disabled={busy}
                options={[
                  { value: 'true', label: 'true' },
                  { value: 'false', label: 'false' },
                ]}
                onChange={(raw) => setStaticValue(raw === 'true' ? 'true' : 'false')}
              />
            ) : (
              <AgentTextField
                id="widget-create-fn-static-value"
                label="Value"
                value={staticValue}
                onChange={(e) => setStaticValue(e.target.value)}
                fullWidth
                multiline={valueType === 'JSON'}
                minRows={valueType === 'JSON' ? 3 : 1}
                placeholder={valueType === 'JSON' ? '{"key":"value"}' : 'Value'}
                disabled={busy}
              />
            )}
          </>
        ) : (
          <>
            <AgentTextField
              id="widget-create-fn-ai-prompt"
              label="AI prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              placeholder="Describe what the function should do…"
              disabled={busy}
            />
            <AgentTextField
              id="widget-create-fn-code"
              label="JavaScript body"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              placeholder="return args[0];"
              disabled={busy}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
            />
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: error ? 1 : 0 }}>
        {fnType === 'JS' && (
          <Button
            variant="outlined"
            size="small"
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
            onClick={() => void handleGenerate()}
            disabled={busy || !aiPrompt.trim()}
          >
            {generating ? 'Generating…' : 'Generate with AI'}
          </Button>
        )}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Button variant="text" size="small" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => void handleSave()}
          disabled={busy || !name.trim()}
        >
          Save function
        </Button>
      </Box>

      {error && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            display: 'block',
            px: 1.25,
            py: 0.85,
            borderRadius: 1,
            color: 'error.main',
            bgcolor: (theme) =>
              alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
            border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
            lineHeight: 1.45,
          }}
        >
          {error}
        </Typography>
      )}

      <Divider sx={{ mt: 1 }} />
    </Box>
  );
};

export default AgentWidgetCreateFunctionForm;
